import { useState } from "react";
import { HeartPulse, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

export function RecoverRestedPanel() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [bulk, setBulk] = useState(null);

  const run = async (dryRun) => {
    if (!email.trim()) { toast.error("Enter a player email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/recover-rested-sessions?email=${encodeURIComponent(email.trim())}&dry_run=${dryRun}`);
      setPreview({ ...data, dryRun });
      const n = data.gladiators?.filter((g) => Object.keys(g.gained || {}).length).length || 0;
      if (dryRun) toast.info(`${n} gladiator(s) would recover training.`);
      else toast.success(`Recovered training for ${data.gladiators?.filter((g) => g.applied).length || 0} gladiator(s).`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const runBulk = async (dryRun) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/recover-rested-sessions-all?dry_run=${dryRun}`);
      setBulk({ ...data, dryRun });
      if (dryRun) toast.info(`${data.players_helped} player(s) / ${data.gladiators_recovered} gladiator(s) would recover.`);
      else toast.success(`Recovered ${data.gladiators_recovered} gladiator(s) across ${data.players_helped} player(s).`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="recover-rested-panel" className="mb-6 border border-rose-900/40 bg-zinc-950 p-4 rounded-sm">
      <div className="flex items-center gap-2 mb-2">
        <HeartPulse size={16} className="text-rose-400" />
        <h3 className="font-cinzel text-rose-300 uppercase tracking-widest text-sm">Recover Wrongly-Rested Training</h3>
      </div>
      <p className="text-[12px] text-zinc-500 mb-3">
        For an active player whose gladiators <span className="text-zinc-300">rested for days</span> because the old inactivity
        pause keyed off a stale login. Rewinds each fighter to when the pause wrongly began and replays those sessions as real
        training. Prime-age rests changed nothing, so recovery is exact. Preview first, then apply.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input data-testid="recover-rested-email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="player@email.com" type="email"
          className="px-3 py-2 text-sm bg-zinc-900 border border-zinc-700 text-zinc-200 rounded-sm focus:border-rose-600 focus:outline-none min-w-[220px]" />
        <button data-testid="recover-rested-preview" onClick={() => run(true)} disabled={busy}
          className="px-4 py-2 text-sm border border-zinc-700 text-zinc-300 hover:border-rose-600 hover:text-rose-300 rounded-sm transition-colors disabled:opacity-50">
          {busy ? "Working…" : "Preview"}
        </button>
        <button data-testid="recover-rested-apply" onClick={() => run(false)} disabled={busy || !preview}
          title={!preview ? "Run a preview first" : "Apply the recovery"}
          className="px-4 py-2 text-sm bg-rose-500 text-zinc-950 font-cinzel uppercase tracking-wide hover:bg-rose-400 rounded-sm transition-colors disabled:opacity-50">
          Apply Recovery
        </button>
      </div>
      {preview && (
        <div data-testid="recover-rested-result" className="mt-3 text-[12px] text-zinc-400 border-t border-zinc-800 pt-3">
          <p><span className="text-zinc-200">{preview.user || preview.email}</span> · {preview.count} gladiator(s){preview.dryRun ? " (preview only)" : " — applied"}</p>
          {preview.note && <p className="flex items-center gap-1.5 text-orange-300 mt-1"><AlertTriangle size={12} /> {preview.note}</p>}
          {preview.gladiators?.length > 0 && (
            <ul className="mt-2 space-y-0.5">
              {preview.gladiators.map((g) => (
                <li key={g.id} className="text-zinc-500">
                  • {g.name}: {Object.keys(g.gained || {}).length
                    ? Object.entries(g.gained).map(([k, v]) => `+${v} ${k}`).join(", ")
                    : "no gain (nothing to recover)"}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-4 border-t border-zinc-800 pt-3">
        <p className="text-[12px] text-zinc-500 mb-2">
          Or sweep <span className="text-rose-300">every affected active player</span> at once (stale login + recently active).
          Genuinely-inactive players are safely skipped.
        </p>
        <div className="flex flex-wrap gap-2">
          <button data-testid="recover-rested-bulk-preview" onClick={() => runBulk(true)} disabled={busy}
            className="px-4 py-2 text-sm border border-zinc-700 text-zinc-300 hover:border-rose-600 hover:text-rose-300 rounded-sm transition-colors disabled:opacity-50">
            {busy ? "Working…" : "Preview ALL affected"}
          </button>
          <button data-testid="recover-rested-bulk-apply" onClick={() => runBulk(false)} disabled={busy || !bulk}
            title={!bulk ? "Run a bulk preview first" : "Apply to all affected players"}
            className="px-4 py-2 text-sm bg-rose-600 text-white font-cinzel uppercase tracking-wide hover:bg-rose-500 rounded-sm transition-colors disabled:opacity-50">
            Recover ALL
          </button>
        </div>
        {bulk && (
          <div data-testid="recover-rested-bulk-result" className="mt-3 text-[12px] text-zinc-400">
            <p><span className="text-zinc-200">{bulk.players_helped}</span> player(s) · <span className="text-zinc-200">{bulk.gladiators_recovered}</span> gladiator(s){bulk.dryRun ? " would recover (preview)" : " recovered"} · {bulk.users_scanned} scanned</p>
            {bulk.samples?.length > 0 && (
              <ul className="mt-2 space-y-0.5">
                {bulk.samples.map((s, i) => (
                  <li key={i} className="text-zinc-500">• <span className="text-zinc-400">{s.user}</span>: {s.gladiators.map((g) => g.name).join(", ")}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
